import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lead, LeadStatus } from '@/types/lead.types';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  StatusBadge,
  getAllStatuses,
  LeadTimeline,
  LeadTaskList,
  LeadNoteList,
} from '@/components/leads';

type TabType = 'overview' | 'timeline' | 'tasks' | 'notes';

const sourceLabels: Record<string, string> = {
  WEBSITE: 'Website',
  INSTAGRAM: 'Instagram',
  META: 'Meta',
  GOOGLE: 'Google',
  EXHIBITION: 'Exhibition',
  MANUAL: 'Manual',
  REFERRAL: 'Referral',
};

export const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const fetchLead = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await leadsService.getById(id);
      // API interceptor unwraps { success: true, data: {...} } to just the data
      // Backend returns { lead: {...} } so extract the lead object
      const leadData = response.data?.lead || response.data;
      setLead(leadData);
    } catch (err) {
      setError('Failed to load lead details');
      console.error('Error fetching lead:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (!id || !lead) return;

    try {
      setIsUpdatingStatus(true);
      await leadsService.updateStatus(id, newStatus);
      setLead({ ...lead, status: newStatus });
      setShowStatusDropdown(false);
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCall = () => {
    if (lead) {
      window.open(`tel:${lead.phone}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    if (lead) {
      window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`, '_blank');
    }
  };

  const handleEmail = () => {
    if (lead?.email) {
      window.open(`mailto:${lead.email}`, '_self');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBudget = (value: number | undefined): string => {
    if (!value) return '-';
    if (value >= 10000000) {
      return `${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `${(value / 100000).toFixed(2)} L`;
    }
    return `Rs. ${value.toLocaleString('en-IN')}`;
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'notes', label: 'Notes' },
  ];

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading lead details...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !lead) {
    return (
      <div className="text-center py-24">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          {error || 'Lead not found'}
        </h3>
        <p className="text-secondary-600 mb-4">
          We could not load the lead details.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/leads')}>
            Back to Leads
          </Button>
          <Button onClick={fetchLead}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/leads')}
        className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Leads
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Lead Info */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-secondary-900">{lead.name}</h1>
              {lead.is_qlead && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-sm font-semibold bg-amber-100 text-amber-800">
                  Q-Lead
                </span>
              )}
            </div>
            <p className="text-secondary-500">#{lead.lead_number}</p>

            <div className="flex items-center gap-4 mt-4">
              {/* Status Badge with Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="inline-flex items-center gap-2"
                  disabled={isUpdatingStatus}
                >
                  <StatusBadge status={lead.status} />
                  <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showStatusDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowStatusDropdown(false)}
                    />
                    <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-20">
                      {getAllStatuses().map((status) => (
                        <button
                          key={status.value}
                          onClick={() => handleStatusChange(status.value)}
                          disabled={status.value === lead.status}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 transition-colors ${
                            status.value === lead.status
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-secondary-700'
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <span className="text-sm text-secondary-500">
                {sourceLabels[lead.source] || lead.source}
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCall}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              }
            >
              Call
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleWhatsApp}
              className="text-green-600 border-green-300 hover:bg-green-50"
              leftIcon={
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              }
            >
              WhatsApp
            </Button>
            {lead.email && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEmail}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              >
                Email
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/leads/${id}/edit`)}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
            >
              Edit
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Contact Information
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-secondary-500">Phone</dt>
                  <dd className="text-sm font-medium text-secondary-900 mt-1">
                    {lead.phone}
                  </dd>
                </div>
                {lead.email && (
                  <div>
                    <dt className="text-sm text-secondary-500">Email</dt>
                    <dd className="text-sm font-medium text-secondary-900 mt-1">
                      {lead.email}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-secondary-500">Source</dt>
                  <dd className="text-sm font-medium text-secondary-900 mt-1">
                    {sourceLabels[lead.source] || lead.source}
                  </dd>
                </div>
              </dl>
            </Card>

            {/* Lead Details */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Lead Details
              </h3>
              <dl className="space-y-4">
                {lead.project_interest && (
                  <div>
                    <dt className="text-sm text-secondary-500">Project Interest</dt>
                    <dd className="text-sm font-medium text-secondary-900 mt-1">
                      {lead.project_interest}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-secondary-500">Budget Range</dt>
                  <dd className="text-sm font-medium text-secondary-900 mt-1">
                    {lead.budget_min || lead.budget_max ? (
                      <>
                        {formatBudget(lead.budget_min)} - {formatBudget(lead.budget_max)}
                      </>
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
                {lead.broker && (
                  <div>
                    <dt className="text-sm text-secondary-500">Assigned Broker</dt>
                    <dd className="text-sm font-medium text-secondary-900 mt-1">
                      {lead.broker.full_name}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>

            {/* Notes */}
            {lead.notes && (
              <Card className="md:col-span-2">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Notes
                </h3>
                <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                  {lead.notes}
                </p>
              </Card>
            )}

            {/* Timestamps */}
            <Card className="md:col-span-2">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Activity
              </h3>
              <dl className="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm text-secondary-500">Created At</dt>
                  <dd className="text-sm font-medium text-secondary-900 mt-1">
                    {formatDate(lead.created_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-secondary-500">Last Updated</dt>
                  <dd className="text-sm font-medium text-secondary-900 mt-1">
                    {formatDate(lead.updated_at)}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <Card>
            <LeadTimeline leadId={id!} />
          </Card>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <Card>
            <LeadTaskList leadId={id!} />
          </Card>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <Card>
            <LeadNoteList leadId={id!} />
          </Card>
        )}
      </div>
    </div>
  );
};

export default LeadDetail;
